import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ExternalLink,
  Tag,
  Copy,
  ChevronLeft,
  ChevronRight,
  Pill,
  Plus,
  Minus,
  Bell,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { getMySupplements } from '@/data/supplements';
import { useGlobalState } from './GlobalStateProvider';
import { useActivityByType } from '@/hooks/useActivityFeed';
import type { SupplementWithEffectiveNotes, SupplementCategory } from '@/types/supplements';

const CATEGORY_COLORS: Record<SupplementCategory, string> = {
  protein: 'bg-muted text-muted-foreground border-border',
  vitamins: 'bg-muted text-muted-foreground border-border',
  recovery: 'bg-muted text-muted-foreground border-border',
  performance: 'bg-muted text-muted-foreground border-border',
  general: 'bg-muted text-muted-foreground border-border',
};

const SUPPLEMENT_EVENT_TYPES = ['supplement_assigned', 'supplement_removed', 'supplement_updated'];
const CHANGES_PAGE_SIZE = 10;

export function ClientSupplementsView() {
  const [supplements, setSupplements] = useState<SupplementWithEffectiveNotes[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [changesOpen, setChangesOpen] = useState(false);
  const [changesPage, setChangesPage] = useState(1);
  const limit = 20;
  const gs = useGlobalState();
  const clientId = gs.authUser?.id || '';

  const { activities: supplementEvents } = useActivityByType(clientId, SUPPLEMENT_EVENT_TYPES, 30, 100);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getMySupplements(page, limit);
      setSupplements(result.data);
      setTotal(result.total);
    } catch (_err: any) {
      toast.error('Failed to load supplements');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMin = Math.floor((now.getTime() - date.getTime()) / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHrs = Math.floor(diffMin / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const totalPages = Math.ceil(total / limit);
  const totalChangesPages = Math.max(1, Math.ceil(supplementEvents.length / CHANGES_PAGE_SIZE));
  const changesStart = (changesPage - 1) * CHANGES_PAGE_SIZE;
  const visibleChanges = supplementEvents.slice(changesStart, changesStart + CHANGES_PAGE_SIZE);

  useEffect(() => {
    if (changesPage > totalChangesPages) {
      setChangesPage(totalChangesPages);
    }
  }, [changesPage, totalChangesPages]);

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Pill className="w-6 h-6" />
          My Supplements
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Supplements recommended by your coach</p>
      </div>

      {/* Recent Changes */}
      {supplementEvents.length > 0 && (
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <button
              type="button"
              onClick={() => setChangesOpen((v) => !v)}
              className="w-full flex items-center justify-between text-left"
            >
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">Recent Changes</h3>
                <Badge className="bg-muted text-muted-foreground border-border">{supplementEvents.length}</Badge>
              </div>
              {changesOpen ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </button>

            {changesOpen && (
              <div className="mt-3 space-y-2">
                {visibleChanges.map((event) => {
                  const isAdded = event.type === 'supplement_assigned';
                  const isRemoved = event.type === 'supplement_removed';
                  return (
                    <div key={event.id} className="flex items-start gap-2.5 text-sm">
                      <div className={`mt-0.5 shrink-0 ${isRemoved ? 'text-rose-400' : 'text-muted-foreground'}`}>
                        {isAdded ? (
                          <Plus className="w-3.5 h-3.5" />
                        ) : isRemoved ? (
                          <Minus className="w-3.5 h-3.5" />
                        ) : (
                          <Pill className="w-3.5 h-3.5" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-foreground truncate">{event.summary}</p>
                        <p className="text-xs text-muted-foreground">{formatTimeAgo(event.created_at)}</p>
                      </div>
                    </div>
                  );
                })}

                {totalChangesPages > 1 && (
                  <div className="flex items-center justify-center gap-3 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={changesPage === 1}
                      onClick={() => setChangesPage((p) => p - 1)}
                      className="h-7 px-2 border-border"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      Page {changesPage} of {totalChangesPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={changesPage >= totalChangesPages}
                      onClick={() => setChangesPage((p) => p + 1)}
                      className="h-7 px-2 border-border"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Content */}
      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="bg-card border-border animate-pulse">
              <CardContent className="p-6 h-40" />
            </Card>
          ))}
        </div>
      ) : supplements.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <Pill className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No supplements assigned yet</h3>
            <p className="text-sm text-muted-foreground">Your coach hasn't assigned any supplements to you yet.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {supplements.map((item) => {
              const supp = item.supplement;
              if (!supp) return null;

              return (
                <Card key={item.id} className="border-border bg-card transition-shadow hover:shadow-md">
                  <CardContent className="flex h-full flex-col gap-3 p-4 md:p-5">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="truncate text-sm font-semibold text-foreground md:text-[15px]">{supp.name}</h3>
                        {supp.brand && <p className="mt-0.5 truncate text-xs text-muted-foreground">{supp.brand}</p>}
                      </div>
                      <Badge
                        variant="outline"
                        className={`shrink-0 text-[11px] capitalize ${CATEGORY_COLORS[supp.category] || CATEGORY_COLORS.general}`}
                      >
                        {supp.category}
                      </Badge>
                    </div>

                    {/* Usage notes */}
                    {item.effective_usage_notes && (
                      <div className="rounded-lg border border-border bg-background/60 px-3 py-2">
                        <p className="text-xs leading-relaxed text-foreground">{item.effective_usage_notes}</p>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-border pt-3">
                      {supp.url && (
                        <a
                          href={supp.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                        >
                          <ExternalLink className="h-3 w-3" />
                          View Product
                        </a>
                      )}
                      {supp.affiliate_code && (
                        <button
                          onClick={() => copyToClipboard(supp.affiliate_code!)}
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                        >
                          <Tag className="h-3 w-3" />
                          <span className="font-mono">{supp.affiliate_code}</span>
                          <Copy className="ml-0.5 h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 pt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="border-border"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="border-border"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
